import Alert from '@mui/material/Alert';
import type { SxProps } from '@mui/material/styles';
import { WhenLoaded } from '@openx/components-core/lib/Loader/WhenLoaded';
import { EMPTY_LIST } from '@openx/constants/api';
import capitalize from 'lodash/capitalize';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealRevenueModifiers } from '../hooks/useDealRevenueModifiers';

const CONTAINER_SX: SxProps = { mt: 1 };

type CommercialAgreementsInfoProps = {
  index: number;
  seats?: string[];
};

export const CommercialAgreementsInfo = ({ index }: CommercialAgreementsInfoProps) => {
  const { loadingAggregate, postAuctionDiscountsAggregate, rebatesAggregate } = useDealRevenueModifiers();
  const { t } = useTranslation();

  const isLoadingCommercialAgreements = loadingAggregate[index];
  const postAuctionDiscounts = postAuctionDiscountsAggregate[index] ?? EMPTY_LIST;
  const rebates = rebatesAggregate[index] ?? EMPTY_LIST;
  const showAlert = postAuctionDiscounts.length !== 0 || rebates.length !== 0;

  const applyText = postAuctionDiscounts.length + rebates.length !== 1 ? t('apply') : t('applies');

  const feeText = useMemo(() => {
    const padText = postAuctionDiscounts.length > 1 ? t('pads') : t('a pad');
    const rebateText = rebates.length > 1 ? t('rebates') : t('a rebate');

    if (postAuctionDiscounts.length !== 0 && rebates.length !== 0) {
      return capitalize(t('{padText} and {rebateText}', { padText, rebateText }));
    }

    if (postAuctionDiscounts.length !== 0) {
      return capitalize(padText);
    }

    if (rebates.length !== 0) {
      return capitalize(rebateText);
    }

    return t('N / A');
  }, [postAuctionDiscounts.length, rebates.length, t]);

  return (
    <WhenLoaded loading={isLoadingCommercialAgreements} noPadding sx={CONTAINER_SX}>
      {showAlert && (
        <Alert severity="info" sx={CONTAINER_SX} data-test="deal-revenue-calculator-commercial-agreements-info">
          {t('{feeText} {applyText} to this Participant, you can view calculations in price breakdown.', {
            applyText,
            feeText,
          })}
        </Alert>
      )}
    </WhenLoaded>
  );
};
